import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import PageLoader from '../components/PageLoader';
import { apiJson, isUnauthorizedError } from '../services/api';
import './StatementReview.css';
import './Documents.css';

const DOC_TYPE_LABELS = {
  manager_report: 'Owner Statement',
  lease: 'Lease',
  insurance: 'Insurance',
  tax: 'Tax',
  inspection: 'Inspection',
  other: 'Other',
};
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const INCOME_CATS = ['rent', 'late_fee', 'pet_fee', 'parking', 'laundry', 'other'];
const EXPENSE_CATS = ['mortgage', 'insurance', 'property_tax', 'repair', 'maintenance',
  'management', 'utilities', 'hoa', 'vacancy', 'capex', 'owner_distribution', 'other'];
const OWNER_DIST = 'owner_distribution';
const getStatementRowKey = (row) => (row?.id != null && row?.row_type ? `${row.row_type}:${row.id}` : null);
const fmtStatementAmount = (n) =>
  n == null || n === '' ? '—' :
  (Number(n) < 0 ? '-' : '') + '$' + Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const getPeriodKey = (period) => {
  if (!period) return null;
  const dt = new Date(period);
  if (Number.isNaN(dt.getTime())) return null;
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
};
const latestCalendarPeriod = (items) => items
  .map(item => getPeriodKey(item?.period_month))
  .filter(Boolean)
  .sort()
  .reverse()[0] || '';
const getDefaultUploadPeriod = () => {
  const now = new Date();
  return {
    period_year: String(now.getFullYear()),
    period_month_number: '',
  };
};

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingDocuments, setDeletingDocuments] = useState(false);
  const [deletingRows, setDeletingRows] = useState(false);
  const [filterProperty, setFilterProperty] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Statement review state
  const [periods, setPeriods] = useState([]);
  const [monthlySummaries, setMonthlySummaries] = useState([]);
  const [activePeriod, setActivePeriod] = useState('');
  const [allRows, setAllRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [reviewPropertyFilter, setReviewPropertyFilter] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedStatementRows, setSelectedStatementRows] = useState(new Set());
  const [confirmRowDelete, setConfirmRowDelete] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [documentUrls, setDocumentUrls] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewUrlLoading, setPreviewUrlLoading] = useState(false);
  const [newRow, setNewRow] = useState({
    property_id: '',
    row_type: 'income',
    date: '',
    category: INCOME_CATS[0],
    amount: null,
    vendor: '',
    description: '',
  });
  const [showAddModal, setShowAddModal] = useState(false);

  // Upload form
  const [form, setForm] = useState(() => ({
    property_ids: [],
    doc_type: 'manager_report',
    ...getDefaultUploadPeriod(),
  }));
  const [selectedFile, setSelectedFile] = useState(null);
  const toast = useRef(null);
  const fileRef = useRef();
  const dtRef = useRef();
  const rowsRequestRef = useRef(0);
  const periodMetadataCacheRef = useRef(new Map());

  function showToast(msg, type = 'success') {
    toast.current?.show({
      severity: type,
      summary: type === 'error' ? 'Error' : type === 'warn' ? 'Warning' : type === 'info' ? 'Info' : 'Success',
      detail: msg,
      life: type === 'error' ? 5000 : 3500,
    });
  }

  function redirectToLogin() {
    navigate('/login', { replace: true });
  }

  function setUploadPeriod(partial) {
    setForm((current) => ({ ...current, ...partial }));
  }

  function setCachedPeriodMetadata(propId, nextPeriods, nextSummaries) {
    const cacheKey = String(propId || '');
    periodMetadataCacheRef.current.set(cacheKey, {
      periods: nextPeriods,
      summaries: nextSummaries,
    });
    setPeriods(nextPeriods);
    setMonthlySummaries(nextSummaries);
    return nextPeriods;
  }

  function clearPeriodMetadataCache() {
    periodMetadataCacheRef.current.clear();
  }

  async function loadPeriodMetadata(propId = reviewPropertyFilter, { force = false } = {}) {
    const cacheKey = String(propId || '');
    if (!force) {
      const cached = periodMetadataCacheRef.current.get(cacheKey);
      if (cached) {
        setPeriods(cached.periods);
        setMonthlySummaries(cached.summaries);
        return cached.periods;
      }
    }

    const suffix = propId ? `?property_id=${propId}` : '';
    const [periodsData, summariesData] = await Promise.all([
      apiJson(`/api/statements/periods${suffix}`),
      apiJson(`/api/statements/monthly-summary${suffix}`),
    ]);
    const nextPeriods = Array.isArray(periodsData) ? periodsData : [];
    const nextSummaries = Array.isArray(summariesData) ? summariesData : [];
    return setCachedPeriodMetadata(propId, nextPeriods, nextSummaries);
  }

  // ── Initial load ───────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [props, docs, periodsData, summaryData] = await Promise.all([
          apiJson('/api/properties'),
          apiJson('/api/documents'),
          apiJson('/api/statements/periods'),
          apiJson('/api/statements/monthly-summary'),
        ]);
        setProperties(Array.isArray(props) ? props : []);
        setForm((current) => ({
          ...current,
          property_ids: (Array.isArray(props) ? props : []).map((property) => String(property.id)),
        }));
        const docList = Array.isArray(docs) ? docs : [];
        setDocuments(docList);
        setDocumentUrls({});
        const pList = Array.isArray(periodsData) ? periodsData : [];
        const summaryList = Array.isArray(summaryData) ? summaryData : [];
        setCachedPeriodMetadata('', pList, summaryList);
        const latestUploadedStatementPeriod = latestCalendarPeriod(
          docList.filter(d => d.doc_type === 'manager_report')
        );
        const initialPeriod = latestUploadedStatementPeriod || pList[0] || '';
        if (initialPeriod) {
          setActivePeriod(initialPeriod);
          loadRows(initialPeriod);
          setNewRow(n => ({
            ...n,
            date: n.row_type === 'income' ? initialPeriod : `${initialPeriod}-01`,
            property_id: reviewPropertyFilter || '',
          }));
        }
        // Default to the newest calendar period among uploaded documents.
        const latestUploadedDocPeriod = latestCalendarPeriod(docList);
        if (latestUploadedDocPeriod) setFilterPeriod(latestUploadedDocPeriod);
      } catch (err) {
        if (isUnauthorizedError(err)) {
          redirectToLogin();
        } else {
          showToast('Failed to load data', 'error');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  async function fetchDocuments() {
    try {
      const docs = await apiJson('/api/documents');
      const nextDocs = Array.isArray(docs) ? docs : [];
      setDocuments(nextDocs);
      setDocumentUrls({});
      setSelected(new Set());
      return nextDocs;
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast('Failed to load documents', 'error');
      }
      return [];
    }
  }

  // ── Load rows for a period ─────────────────────────────────────────
  async function loadRows(period, propId = reviewPropertyFilter) {
    const requestId = rowsRequestRef.current + 1;
    rowsRequestRef.current = requestId;
    if (!period) {
      setAllRows([]);
      setRowsLoading(false);
      return;
    }
    setRowsLoading(true);
    try {
      let url = `/api/statements/rows?period=${period}`;
      if (propId) url += `&property_id=${propId}`;
      const rows = await apiJson(url);
      if (rowsRequestRef.current === requestId) {
        setAllRows(Array.isArray(rows) ? rows : []);
      }
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast('Failed to load statement rows', 'error');
      }
      if (rowsRequestRef.current === requestId) {
        setAllRows([]);
      }
    } finally {
      if (rowsRequestRef.current === requestId) {
        setRowsLoading(false);
      }
    }
  }

  async function refreshPeriods(currentPeriod = activePeriod, propId = reviewPropertyFilter, { force = false, reloadRows = true } = {}) {
    try {
      const list = await loadPeriodMetadata(propId, { force });
      const nextPeriod = list.includes(currentPeriod) ? currentPeriod : (list[0] || '');
      if (nextPeriod) {
        setActivePeriod(nextPeriod);
        // Always reload rows after an upload/delete — data may have changed even if period is the same
        if (reloadRows) await loadRows(nextPeriod, propId);
        return nextPeriod;
      } else {
        setActivePeriod('');
        setAllRows([]);
        return '';
      }
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast('Failed to refresh periods', 'error');
      }
      return '';
    }
  }

  function resetNewRow(rowType = 'income') {
    setNewRow({
      property_id: reviewPropertyFilter || '',
      row_type: rowType,
      date: rowType === 'income'
        ? (activePeriod || '')
        : (activePeriod ? `${activePeriod}-01` : ''),
      category: rowType === 'income' ? INCOME_CATS[0] : EXPENSE_CATS[0],
      amount: null,
      vendor: '',
      description: '',
    });
  }

  function handlePeriodChange(period) {
    setActivePeriod(period);
    setFilterPeriod(period);
    setPreviewDocId(null);
    setNewRow(r => ({
      ...r,
      date: r.row_type === 'income'
        ? period
        : (period ? `${period}-01` : r.date),
    }));
  }

  async function handleReviewPropertyFilter(propId) {
    setReviewPropertyFilter(propId);
    setFilterProperty(propId);
    setPreviewDocId(null);
    setSelected(new Set());
    setNewRow(r => ({ ...r, property_id: propId || '' }));
    const nextPeriod = await refreshPeriods(filterPeriod || activePeriod, propId, { reloadRows: false });
    if (nextPeriod && nextPeriod !== filterPeriod) setFilterPeriod(nextPeriod);
  }

  function handleDocumentPeriodChange(period) {
    setFilterPeriod(period);
    setActivePeriod(period);
    setPreviewDocId(null);
    setSelectedStatementRows(new Set());
    setNewRow(r => ({
      ...r,
      date: r.row_type === 'income'
        ? period
        : (period ? `${period}-01` : r.date),
    }));
    if (period) {
      return;
    } else {
      refreshPeriods(activePeriod, reviewPropertyFilter);
    }
  }

  async function handleDocumentPropertyChange(propId) {
    setFilterProperty(propId);
    setReviewPropertyFilter(propId);
    setPreviewDocId(null);
    setSelected(new Set());
    setSelectedStatementRows(new Set());
    setNewRow(r => ({ ...r, property_id: propId || '' }));
    const nextPeriod = await refreshPeriods(filterPeriod || activePeriod, propId, { reloadRows: false });
    if (nextPeriod && nextPeriod !== filterPeriod) setFilterPeriod(nextPeriod);
  }

  // ── Inline cell save ───────────────────────────────────────────────
  async function onCellEditComplete(e) {
    const { rowData, newValue, field } = e;
    if (newValue === rowData[field]) return;
    const updated = { ...rowData, [field]: newValue };
    try {
      await apiJson(`/api/document-rows/${rowData.row_type}/${rowData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      setAllRows(prev => prev.map(r =>
        r.id === rowData.id && r.row_type === rowData.row_type ? updated : r
      ));
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast('Save failed', 'error');
      }
    }
  }

  // ── CSV export ─────────────────────────────────────────────────────
  function exportCSV() {
    if (!displayedRows.length) return;
    const csvCell = (val) => `"${String(val ?? '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""')}"`;
    const hdrs = ['Property', 'Date', 'Type', 'Category', 'Amount', 'Vendor', 'Description'];
    const lines = displayedRows.map(r => [
      csvCell(r.property),
      r.date || '',
      r.row_type || '',
      r.category || '',
      r.amount != null ? r.amount : '',
      csvCell(r.vendor),
      csvCell(r.description),
    ].join(','));
    const csv = [hdrs.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement_${activePeriod || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Copy to clipboard ──────────────────────────────────────────────
  function copyToClipboard() {
    if (!displayedRows.length) return;
    const clean = (val) => String(val ?? '').replace(/[\r\n\t]+/g, ' ').trim();
    const hdrs = ['Property', 'Date', 'Type', 'Category', 'Amount', 'Vendor', 'Description'];
    const lines = displayedRows.map(r => [
      clean(r.property),
      clean(r.date),
      clean(r.row_type),
      clean(r.category),
      r.amount != null ? r.amount : '',
      clean(r.vendor),
      clean(r.description),
    ].join('\t'));
    const text = [hdrs.join('\t'), ...lines].join('\n');
    navigator.clipboard.writeText(text).then(
      () => showToast('Copied to clipboard'),
      () => showToast('Copy failed', 'error')
    );
  }

  // ── Add manual row ───────────────────────────────────────────────
  async function handleAddRow(e) {
    if (e) e.preventDefault();
    if (!newRow.property_id) { showToast('Select a property', 'error'); return; }
    if (!newRow.date) { showToast('Select a date/month', 'error'); return; }
    if (newRow.amount == null || Number.isNaN(parseFloat(newRow.amount))) {
      showToast('Enter a valid amount', 'error'); return;
    }
    const propertyId = newRow.property_id;
    const amount = parseFloat(newRow.amount);
    try {
      if (newRow.row_type === 'income') {
        const periodMonth = newRow.date ? `${newRow.date.slice(0, 7)}-01` : '';
        const body = {
          period_month: periodMonth,
          amount,
          category: newRow.category,
          notes: newRow.description,
        };
        await apiJson(`/api/properties/${propertyId}/income`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        const body = {
          expense_date: newRow.date.slice(0, 10),
          amount,
          category: newRow.category,
          vendor: newRow.vendor,
          description: newRow.description,
        };
        await apiJson(`/api/properties/${propertyId}/expenses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      showToast('Row added');
      resetNewRow(newRow.row_type);
      setShowAddModal(false);
      await loadRows(activePeriod, reviewPropertyFilter);
      clearPeriodMetadataCache();
      await refreshPeriods(activePeriod, reviewPropertyFilter, { force: true });
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast(err.message || 'Add failed', 'error');
      }
    }
  }

  // ── Doc list filter ────────────────────────────────────────────────
  const fmtPeriod = (p) => {
    if (!p) return '—';
    const d = new Date(p);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };

  const toPeriodKey = (p) => {
    if (!p) return null;
    return getPeriodKey(p);
  };

  const filteredDocuments = filterProperty
    ? documents.filter(d => String(d.property_id) === String(filterProperty))
    : documents;

  const docPeriods = [...new Set(
    filteredDocuments.map(d => toPeriodKey(d.period_month)).filter(Boolean)
  )].sort().reverse();

  const displayedDocs = (() => {
    let filtered = filteredDocuments;
    if (filterPeriod) filtered = filtered.filter(d => toPeriodKey(d.period_month) === filterPeriod);
    if (!showAll) {
      const seen = new Set();
      const result = [];
      for (const d of filtered) {
        if (!seen.has(d.property_id)) { seen.add(d.property_id); result.push(d); }
      }
      return result;
    }
    return filtered;
  })();

  const statementDocs = documents
    .filter(d => d.doc_type === 'manager_report' && (!reviewPropertyFilter || String(d.property_id) === String(reviewPropertyFilter)))
    .sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
  const availablePeriods = [...new Set([
    ...periods,
    ...monthlySummaries.map(summary => summary.period).filter(Boolean),
    ...statementDocs.map(doc => toPeriodKey(doc.period_month)).filter(Boolean),
  ])].sort().reverse();
  const selectedPeriodDocs = activePeriod
    ? statementDocs.filter(d => toPeriodKey(d.period_month) === activePeriod)
      .sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0))
    : [];
  const previewDoc = selectedPeriodDocs.find(doc => String(doc.id) === String(previewDocId)) || selectedPeriodDocs[0] || null;
  const monthlySummaryByPeriod = new Map(
    monthlySummaries.map(summary => [summary.period, summary])
  );

  const incomeRows = allRows.filter(r => r.row_type === 'income');
  const expenseRows = allRows.filter(r => r.row_type === 'expense' && r.category !== OWNER_DIST);
  const ownerDistributionRows = allRows.filter(r => r.row_type === 'expense' && r.category === OWNER_DIST);
  const manageableExpenseRows = [...expenseRows, ...ownerDistributionRows].sort((a, b) => {
    const propertySort = String(a.property || '').localeCompare(String(b.property || ''));
    if (propertySort !== 0) return propertySort;
    return String(a.date || '').localeCompare(String(b.date || ''));
  });
  const statementRows = [...incomeRows, ...expenseRows].sort((a, b) => {
    const propertySort = String(a.property || '').localeCompare(String(b.property || ''));
    if (propertySort !== 0) return propertySort;
    return String(a.date || '').localeCompare(String(b.date || ''));
  });
  const displayedRows = activeTab === 'income'
    ? incomeRows
    : activeTab === 'expenses'
      ? manageableExpenseRows
      : statementRows;
  const allUploadPropertyIds = properties.map((property) => String(property.id));
  const allUploadPropertiesSelected = properties.length > 0 && form.property_ids.length === properties.length;
  const uploadPropertySelectValue = allUploadPropertiesSelected ? '__all__' : '';
  const selectedVisibleStatementRows = displayedRows.filter((row) => selectedStatementRows.has(getStatementRowKey(row)));
  const allVisibleStatementRowsSelected = displayedRows.length > 0 && selectedVisibleStatementRows.length === displayedRows.length;
  const someVisibleStatementRowsSelected = selectedVisibleStatementRows.length > 0;

  // ── Totals ────────────────────────────────────────────────────────
  const incomeTotal = incomeRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const expenseTotal = expenseRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const ownerDistributionTotal = ownerDistributionRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  const netTotal = incomeTotal - expenseTotal;
  const availablePeriodsKey = availablePeriods.join('|');
  const selectedPeriodDocIdsKey = selectedPeriodDocs.map(doc => String(doc.id)).join('|');

  useEffect(() => {
    if (availablePeriods.length === 0) {
      if (activePeriod) {
        setActivePeriod('');
        setAllRows([]);
      }
      return;
    }
    if (!activePeriod || !availablePeriods.includes(activePeriod)) {
      const nextPeriod = availablePeriods[0];
      setActivePeriod(nextPeriod);
      setNewRow(r => ({
        ...r,
        property_id: reviewPropertyFilter || r.property_id || '',
        date: r.row_type === 'income' ? nextPeriod : `${nextPeriod}-01`,
      }));
    }
  }, [availablePeriodsKey, reviewPropertyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (docPeriods.length === 0) {
      if (filterPeriod) setFilterPeriod('');
      return;
    }
    if (!filterPeriod || !docPeriods.includes(filterPeriod)) {
      setFilterPeriod(docPeriods[0]);
    }
  }, [docPeriods.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activePeriod) {
      rowsRequestRef.current += 1;
      setAllRows([]);
      setRowsLoading(false);
      return;
    }
    loadRows(activePeriod, reviewPropertyFilter);
  }, [activePeriod, reviewPropertyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedPeriodDocs.length === 0) {
      if (previewDocId != null) setPreviewDocId(null);
      return;
    }
    const hasCurrentPreview = selectedPeriodDocs.some(doc => String(doc.id) === String(previewDocId));
    if (!hasCurrentPreview) {
      setPreviewDocId(selectedPeriodDocs[0].id);
    }
  }, [selectedPeriodDocIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    if (!previewDoc) {
      setPreviewUrl('');
      setPreviewUrlLoading(false);
      return () => { cancelled = true; };
    }

    const cachedUrl = getCachedDocumentUrl(previewDoc.id);
    if (cachedUrl) {
      setPreviewUrl(cachedUrl);
      setPreviewUrlLoading(false);
      return () => { cancelled = true; };
    }

    setPreviewUrl('');
    setPreviewUrlLoading(true);
    resolveDocumentUrl(previewDoc, { silent: true })
      .then(url => {
        if (!cancelled) setPreviewUrl(url || previewDoc.url || '');
      })
      .finally(() => {
        if (!cancelled) setPreviewUrlLoading(false);
      });
    return () => { cancelled = true; };
  }, [previewDoc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedStatementRows(new Set());
  }, [activeTab, activePeriod, reviewPropertyFilter]);

  // ── Selection ──────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleSelectAll() {
    setSelected(selected.size === displayedDocs.length ? new Set() : new Set(displayedDocs.map(d => d.id)));
  }
  const allSelected = displayedDocs.length > 0 && selected.size === displayedDocs.length;
  const someSelected = selected.size > 0;
  const selectedDocs = displayedDocs.filter(d => selected.has(d.id));

  function toggleStatementRowSelection(row) {
    const rowKey = getStatementRowKey(row);
    if (!rowKey) return;
    setSelectedStatementRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }

  function toggleAllStatementRows() {
    if (allVisibleStatementRowsSelected) {
      setSelectedStatementRows(new Set());
      return;
    }
    setSelectedStatementRows(new Set(displayedRows.map(getStatementRowKey).filter(Boolean)));
  }

  // ── Download ───────────────────────────────────────────────────────
  function getCachedDocumentUrl(docId) {
    const cached = documentUrls[docId];
    if (!cached) return null;
    if (cached.expiresAt && cached.expiresAt <= Date.now() + 30000) return null;
    return cached.url;
  }

  async function resolveDocumentUrl(doc, { silent = false } = {}) {
    if (!doc?.id) return doc?.url || null;
    const cachedUrl = getCachedDocumentUrl(doc.id);
    if (cachedUrl) return cachedUrl;
    try {
      const data = await apiJson(`/api/documents/${doc.id}/url`);
      const url = data?.url || doc.url || null;
      if (url) {
        const expiresMs = data?.expires_in ? Number(data.expires_in) * 1000 : 5 * 60 * 1000;
        setDocumentUrls(prev => ({
          ...prev,
          [doc.id]: { url, expiresAt: Date.now() + expiresMs },
        }));
      }
      return url;
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
        return null;
      }
      if (!silent) showToast('Could not prepare a secure document link', 'warn');
      return doc.url || null;
    }
  }

  async function openDocument(doc) {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    const url = await resolveDocumentUrl(doc);
    if (url && popup) popup.location.href = url;
    else if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else if (popup) popup.close();
  }

  async function downloadDocs(docs) {
    for (let i = 0; i < docs.length; i += 1) {
      if (i > 0) await new Promise(resolve => setTimeout(resolve, 300));
      const doc = docs[i];
      const url = await resolveDocumentUrl(doc, { silent: docs.length > 1 });
      if (!url) continue;
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename;
      a.target = '_blank'; a.rel = 'noreferrer';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────
  async function doDelete() {
    const targets = documents.filter(d => selected.has(d.id));
    if (!targets.length) {
      setConfirmDelete(null);
      return;
    }
    setDeletingDocuments(true);
    let deleted = 0;
    try {
      for (const doc of targets) {
        try {
          await apiJson(`/api/documents/${doc.id}`, { method: 'DELETE' });
          deleted++;
        } catch (err) {
          if (isUnauthorizedError(err)) {
            redirectToLogin();
            return;
          }
        }
      }
      showToast(`${deleted} document${deleted !== 1 ? 's' : ''} deleted`);
      clearPeriodMetadataCache();
      const nextDocs = await fetchDocuments();
      const nextFilteredDocs = filterProperty
        ? nextDocs.filter(d => String(d.property_id) === String(filterProperty))
        : nextDocs;
      const nextDocPeriods = [...new Set(
        nextFilteredDocs.map(d => toPeriodKey(d.period_month)).filter(Boolean)
      )].sort().reverse();
      const nextFilterPeriod = nextDocPeriods.includes(filterPeriod) ? filterPeriod : (nextDocPeriods[0] || '');
      setFilterPeriod(nextFilterPeriod);
      await refreshPeriods(nextFilterPeriod || activePeriod, reviewPropertyFilter, { force: true });
    } finally {
      setConfirmDelete(null);
      setDeletingDocuments(false);
    }
  }

  async function doDeleteRows() {
    if (!confirmRowDelete?.rows?.length) return;
    const rowsToDelete = confirmRowDelete.rows.filter((row) => row?.id != null);
    setConfirmRowDelete(null);
    setDeletingRows(true);
    let deleted = 0;
    try {
      for (const row of rowsToDelete) {
        try {
          await apiJson(`/api/document-rows/${row.row_type}/${row.id}`, { method: 'DELETE' });
          deleted++;
        } catch (err) {
          if (isUnauthorizedError(err)) {
            redirectToLogin();
            return;
          }
        }
      }
      setSelectedStatementRows(new Set());
      showToast(`${deleted} row${deleted !== 1 ? 's' : ''} deleted`);
      clearPeriodMetadataCache();
      await refreshPeriods(activePeriod, reviewPropertyFilter, { force: true });
    } finally {
      setDeletingRows(false);
    }
  }

  // ── Upload ─────────────────────────────────────────────────────────
  function toggleProperty(pid) {
    setForm(f => ({
      ...f,
      property_ids: f.property_ids.includes(pid) ? f.property_ids.filter(x => x !== pid) : [...f.property_ids, pid],
    }));
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!selectedFile) { showToast('Please select a PDF file', 'error'); return; }
    if (form.property_ids.length === 0) { showToast('Please select at least one property', 'error'); return; }
    if (!form.period_month_number) { showToast('Please select a month before uploading', 'error'); return; }
    const requestedPeriod = form.period_year && form.period_month_number
      ? `${form.period_year}-${form.period_month_number}`
      : '';
    setUploading(true);
    const fd = new FormData();
    fd.append('file', selectedFile);
    form.property_ids.forEach(pid => fd.append('property_ids', pid));
    fd.append('doc_type', form.doc_type);
    if (requestedPeriod) fd.append('period_month', requestedPeriod + '-01');
    try {
      const data = await apiJson('/api/documents/upload', { method: 'POST', body: fd });
      const s = data.parse_summary;
      if (s && !s.error) {
        showToast(`Uploaded! ${s.income_added} income + ${s.expense_added} expense rows added`);
      } else if (s?.error) {
        showToast(`Uploaded, but parsing failed: ${s.error}`, 'error');
      } else {
        showToast('Document uploaded successfully');
      }
      const uploadedPeriod = data.uploaded_period || (requestedPeriod ? requestedPeriod.slice(0, 7) : '');
      if (data.ids?.length) {
        setPreviewDocId(data.ids[0]);
      }
      if (uploadedPeriod) {
        setActivePeriod(uploadedPeriod);
        setFilterPeriod(uploadedPeriod);
        setActiveTab('summary');
      }
      setSelectedFile(null);
      setForm({
        property_ids: allUploadPropertyIds,
        doc_type: 'manager_report',
        ...getDefaultUploadPeriod(),
      });
      if (fileRef.current) fileRef.current.value = '';
      clearPeriodMetadataCache();
      await fetchDocuments();
      await refreshPeriods(uploadedPeriod || activePeriod, reviewPropertyFilter, { force: true });
    } catch (err) {
      if (isUnauthorizedError(err)) {
        redirectToLogin();
      } else {
        showToast(err.message || 'Upload failed', 'error');
      }
    } finally {
      setUploading(false);
    }
  }

  function propertyLabel(doc) {
    return doc.nickname || doc.address_line1 || `Property #${doc.property_id}`;
  }

  // ── Helpers ────────────────────────────────────────────────────────
  const fmtCat = (cat) => (cat || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const fmtAmount = (row) => row.amount != null
    ? (
      <span className={`docs-amount ${row.category === OWNER_DIST ? 'docs-amount--owner' : ''}`}>
        ${Number(row.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </span>
    ) : '—';
  // ── DataTable editors ──────────────────────────────────────────────
  const textEditor = (options) => (
    <InputText value={options.value ?? ''} onChange={e => options.editorCallback(e.target.value)}
      style={{ width: '100%', fontSize: 13 }} />
  );
  const numberEditor = (options) => (
    <InputNumber value={options.value} onValueChange={e => options.editorCallback(e.value)}
      mode="decimal" minFractionDigits={2} maxFractionDigits={2} style={{ width: '100%' }} />
  );
  const categoryEditor = (options) => {
    const cats = options.rowData.row_type === 'income' ? INCOME_CATS : EXPENSE_CATS;
    return (
      <Dropdown value={options.value}
        options={cats.map(c => ({ label: fmtCat(c), value: c }))}
        onChange={e => options.editorCallback(e.value)} style={{ width: '100%', fontSize: 13 }} />
    );
  };

  if (loading) return <div className="docs-page"><PageLoader label="Loading documents" /></div>;

  return (
    <div className="docs-page">
      <Toast ref={toast} position="top-right" />

      {/* ── Upload overlay ── */}
      {uploading && (
        <div className="docs-upload-overlay">
          <div className="docs-upload-overlay-box">
            <ProgressSpinner className="docs-upload-spinner" />
            <p className="docs-upload-overlay-title">Uploading & Parsing PDF</p>
            <p className="docs-upload-overlay-sub">Extracting transaction data — this may take 10–30 seconds…</p>
          </div>
        </div>
      )}

      {deletingDocuments && (
        <div className="docs-upload-overlay">
          <div className="docs-upload-overlay-box">
            <ProgressSpinner className="docs-upload-spinner" />
            <p className="docs-upload-overlay-title">Deleting Documents</p>
            <p className="docs-upload-overlay-sub">Refreshing the latest document set for the selected period…</p>
          </div>
        </div>
      )}

      {deletingRows && (
        <div className="docs-upload-overlay">
          <div className="docs-upload-overlay-box">
            <ProgressSpinner className="docs-upload-spinner" />
            <p className="docs-upload-overlay-title">Deleting Rows</p>
            <p className="docs-upload-overlay-sub">Recomputing statement data for the current period…</p>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="docs-modal-backdrop">
          <div className="docs-modal">
            {deletingDocuments ? (
              <div className="docs-modal-loading">
                <ProgressSpinner className="docs-modal-spinner" />
                <p className="docs-modal-loading-title">Deleting documents</p>
                <p className="docs-modal-loading-sub">Removing PDFs and refreshing the document list…</p>
              </div>
            ) : (
              <>
                <p>Delete {selected.size} selected document{selected.size !== 1 ? 's' : ''}? This cannot be undone.</p>
                <div className="docs-modal-actions">
                  <button className="docs-modal-btn docs-modal-btn--del" onClick={doDelete}>Delete</button>
                  <button className="docs-modal-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmRowDelete && (
        <div className="docs-modal-backdrop">
          <div className="docs-modal">
            <p>
              Delete {confirmRowDelete.rows.length} selected row{confirmRowDelete.rows.length !== 1 ? 's' : ''}?
              This cannot be undone.
            </p>
            <div className="docs-modal-actions">
              <button className="docs-modal-btn docs-modal-btn--del" onClick={doDeleteRows}>Delete</button>
              <button className="docs-modal-btn" onClick={() => setConfirmRowDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="docs-shell">
        <div className="docs-header">
          <div>
            <p className="docs-eyebrow">Property Management</p>
            <h1>Documents</h1>
          </div>
        </div>

        {/* ── Upload card ── */}
        <div className="docs-upload-card">
          <h2 className="docs-section-title">Upload Statement or Document</h2>
          <form className="docs-upload-form" onSubmit={handleUpload}>
            <div className="docs-form-row">
              <div className="docs-field">
                <label>Properties *</label>
                <select value={uploadPropertySelectValue} onChange={e => {
                  if (e.target.value === '__all__') setForm(f => ({ ...f, property_ids: allUploadPropertyIds }));
                  else if (e.target.value) toggleProperty(e.target.value);
                }}>
                  <option value="">{allUploadPropertiesSelected ? 'All properties selected' : 'Select property…'}</option>
                  <option value="__all__">— All Properties —</option>
                  {properties.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {form.property_ids.includes(String(p.id)) ? '✓ ' : ''}{p.nickname || p.address_line1}
                    </option>
                  ))}
                </select>
                {form.property_ids.length > 0 && (
                  <div className="docs-selected-tags">
                    {allUploadPropertiesSelected ? (
                      <span className="docs-tag">
                        All properties selected ({properties.length})
                        <button type="button" onClick={() => setForm(f => ({ ...f, property_ids: [] }))}>×</button>
                      </span>
                    ) : form.property_ids.map(pid => {
                      const p = properties.find(x => String(x.id) === pid);
                      return (
                        <span key={pid} className="docs-tag">
                          {p?.nickname || p?.address_line1}
                          <button type="button" onClick={() => toggleProperty(pid)}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="docs-field">
                <label>Document Type</label>
                <select value={form.doc_type} onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))}>
                  {Object.entries(DOC_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="docs-field">
                <label>Period (Month)</label>
                <div className="docs-period-selects">
                  <select
                    value={form.period_month_number}
                    onChange={e => setUploadPeriod({ period_month_number: e.target.value })}
                  >
                    <option value="">Month</option>
                    {MONTH_OPTIONS.map((month) => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                  <input value={form.period_year} readOnly aria-label="Year" />
                </div>
              </div>
            </div>
            <div className="docs-file-row">
              <div className="docs-dropzone" onClick={() => fileRef.current.click()}>
                <i className="pi pi-file-pdf" />
                <span>{selectedFile ? selectedFile.name : 'Click to select a PDF'}</span>
                {selectedFile && <span className="docs-file-size">{(selectedFile.size / 1024).toFixed(1)} KB</span>}
              </div>
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
                onChange={e => setSelectedFile(e.target.files[0] || null)} />
              <button type="submit" className="docs-upload-btn" disabled={uploading}>
                <i className="pi pi-upload" /> Upload
              </button>
            </div>
          </form>
        </div>

        {/* ── Document list ── */}
        <div className="docs-list-card">
          <div className="docs-list-header">
            <div className="docs-list-header-left">
              <h2 className="docs-section-title">
                Uploaded Documents
                {someSelected && <span className="docs-selected-count">{selected.size} selected</span>}
              </h2>
              {/* <button className="docs-toggle-btn" onClick={() => setShowAll(v => !v)}>
                {showAll ? 'Show Most Recent' : 'Show All'}
              </button> */}
            </div>
            <div className="docs-list-actions">
              {someSelected && (
                <>
                  <button className="docs-bulk-btn docs-bulk-btn--download" onClick={() => downloadDocs(selectedDocs)}>
                    <i className="pi pi-download" /> Download ({selected.size})
                  </button>
                  <button className="docs-bulk-btn docs-bulk-btn--delete" onClick={() => setConfirmDelete('bulk')}>
                    <i className="pi pi-trash" /> Delete ({selected.size})
                  </button>
                </>
              )}
              <select className="docs-filter" value={filterPeriod} onChange={e => handleDocumentPeriodChange(e.target.value)}>
                {docPeriods.map(p => <option key={p} value={p}>{fmtPeriod(p + '-01T12:00:00Z')}</option>)}
              </select>
              <select className="docs-filter" value={filterProperty} onChange={e => handleDocumentPropertyChange(e.target.value)}>
                <option value="">All properties</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.nickname || p.address_line1}</option>)}
              </select>
            </div>
          </div>

          {displayedDocs.length === 0 ? (
            <div className="docs-empty">
              <i className="pi pi-folder-open" />
              <p>No documents uploaded yet.</p>
            </div>
          ) : (
            <table className="docs-table">
              <thead>
                <tr>
                  <th className="docs-th-check">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th>File</th>
                  <th>Property</th>
                  <th>Type</th>
                  <th>Period</th>
                  <th>Uploaded</th>
                  <th>Parsed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {displayedDocs.map(doc => (
                  <Fragment key={doc.id}>
                    <tr className={selected.has(doc.id) ? 'docs-row--selected' : ''}>
                      <td className="docs-td-check">
                        <input type="checkbox" checked={selected.has(doc.id)} onChange={() => toggleSelect(doc.id)} />
                      </td>
                      <td>
                        <span className="docs-file-name">
                          <i className="pi pi-file-pdf" /> {doc.filename}
                        </span>
                      </td>
                      <td>{propertyLabel(doc)}</td>
                      <td><span className="docs-type-badge">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</span></td>
                      <td>{(() => {
                        if (!doc.period_month) return '—';
                        const d = new Date(doc.period_month);
                        if (isNaN(d)) return '—';
                        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
                      })()}</td>
                      <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</td>
                      <td>
                        <span className={`docs-parsed-badge ${doc.parsed ? 'docs-parsed-badge--yes' : 'docs-parsed-badge--no'}`}>
                          {doc.parsed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="docs-row-actions">
                        <button type="button" className="docs-icon-btn" onClick={() => openDocument(doc)} title="Open PDF">
                          <i className="pi pi-external-link" />
                        </button>
                        <button className="docs-icon-btn" onClick={() => downloadDocs([doc])} title="Download">
                          <i className="pi pi-download" />
                        </button>
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {previewDoc && (
          <div className="docs-pdf-card docs-pdf-card--statement">
            <div className="docs-pdf-header">
              <div className="docs-pdf-title">
                <i className="pi pi-file-pdf" />
                <div>
                  <p className="docs-pdf-eyebrow">
                    Statement Preview
                    {activePeriod ? ` • ${new Date(`${activePeriod}-02T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : ''}
                  </p>
                  <p className="docs-pdf-name">{previewDoc.filename}</p>
                  <p className="docs-pdf-meta">{propertyLabel(previewDoc)}</p>
                </div>
              </div>

              <div className="docs-pdf-actions">
                {selectedPeriodDocs.length > 1 && (
                  <select
                    className="docs-pdf-select"
                    value={previewDoc.id}
                    onChange={(event) => setPreviewDocId(event.target.value)}
                  >
                    {selectedPeriodDocs.map(doc => (
                      <option key={doc.id} value={doc.id}>
                        {propertyLabel(doc)} • {doc.filename}
                      </option>
                    ))}
                  </select>
                )}
                <button type="button" className="docs-pdf-open" onClick={() => openDocument(previewDoc)}>
                  <i className="pi pi-external-link" /> Open
                </button>
                <button type="button" className="docs-pdf-download" onClick={() => downloadDocs([previewDoc])}>
                  <i className="pi pi-download" /> Download
                </button>
              </div>
            </div>
            {previewUrlLoading && <p className="docs-preview-loading">Preparing secure preview…</p>}
            <iframe className="docs-pdf-iframe" src={previewUrl || 'about:blank'} title={`${previewDoc.filename} preview`} />
          </div>
        )}

        {/* ── Statement Analysis ── */}
        <div className="docs-review-panel docs-review-panel--statement">
          <div className="sr-header docs-sr-header">
            <div className="sr-header-left">
              <div>
                <p className="sr-eyebrow">Statement Analysis</p>
                <h2 className="docs-statement-title">
                  {reviewPropertyFilter
                    ? (properties.find(p => String(p.id) === String(reviewPropertyFilter))?.nickname
                      || properties.find(p => String(p.id) === String(reviewPropertyFilter))?.address_line1
                      || 'Property Statement View')
                    : 'Portfolio Statement View'}
                </h2>
              </div>
            </div>

            <div className="sr-header-right docs-sr-controls">
              {/* <button className="sr-upload-btn" onClick={openAddModal}>
                <i className="pi pi-plus" /> Add Row
              </button> */}
              <div className="docs-statement-filter">
                <label className="sr-month-label">Viewing period</label>
                <select className="sr-month-select docs-statement-select" value={activePeriod} onChange={e => handlePeriodChange(e.target.value)}>
                  {availablePeriods.length === 0 ? (
                    <option value="">No periods available</option>
                  ) : (
                    availablePeriods.map(period => (
                      <option key={period} value={period}>
                        {new Date(`${period}-02T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="docs-statement-filter">
                <label className="sr-month-label">Property</label>
                <select className="sr-month-select docs-statement-select" value={reviewPropertyFilter} onChange={e => handleReviewPropertyFilter(e.target.value)}>
                  <option value="">All properties</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.nickname || p.address_line1}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="sr-summary">
            <div className="sr-sum-item">
              <span className="sr-sum-label">Income</span>
              <span className="sr-sum-val sr-green">{fmtStatementAmount(incomeTotal)}</span>
            </div>
            <div className="sr-sum-item">
              <span className="sr-sum-label">Expenses</span>
              <span className="sr-sum-val sr-red">{fmtStatementAmount(expenseTotal)}</span>
            </div>
            <div className="sr-sum-item">
              <span className="sr-sum-label">Net</span>
              <span className={`sr-sum-val ${netTotal >= 0 ? 'sr-green' : 'sr-red'}`}>{fmtStatementAmount(netTotal)}</span>
            </div>
            {ownerDistributionTotal > 0 && (
              <div className="sr-sum-item">
                <span className="sr-sum-label">Owner Distribution</span>
                <span className="sr-sum-val sr-owner">{fmtStatementAmount(ownerDistributionTotal)}</span>
              </div>
            )}
            <div className="sr-sum-item sr-sum-item--doc">
              <i className="pi pi-file-pdf" />
              <span>
                {selectedPeriodDocs.length === 0
                  ? 'No statements for this period'
                  : reviewPropertyFilter && previewDoc
                    ? previewDoc.filename
                    : `${selectedPeriodDocs.length} statement${selectedPeriodDocs.length === 1 ? '' : 's'} this period`}
              </span>
            </div>
          </div>

          <div className="sr-body--full">
            <div className="sr-tables-panel">
              <div className="sr-tabs">
                {[
                  { key: 'summary', label: 'Summary' },
                  { key: 'statement', label: 'Statement', count: statementRows.length },
                  { key: 'income', label: 'Income', count: incomeRows.length },
                  { key: 'expenses', label: 'Expenses', count: manageableExpenseRows.length },
                  { key: 'documents', label: 'Documents', count: selectedPeriodDocs.length },
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`sr-tab ${activeTab === tab.key ? 'sr-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    {tab.count != null && tab.key !== 'summary' && <span className="sr-tab-count">{tab.count}</span>}
                  </button>
                ))}
              </div>

              {activeTab === 'summary' && (
                <div className="sr-table-section">
                  <div className="sr-summary-grid">
                    <div className="sr-summary-col">
                      <div className="sr-summary-col-header sr-summary-col-header--income">
                        <span>Income</span>
                        <span className="sr-green">{fmtStatementAmount(incomeTotal)}</span>
                      </div>
                      {incomeRows.length === 0 ? (
                        <p className="sr-empty">No income this period.</p>
                      ) : (
                        <div className="sr-table-wrap">
                          <table className="sr-table">
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Month</th>
                                <th>Category</th>
                                <th>Amount</th>
                                <th>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {incomeRows.map(row => (
                                <tr key={`income-${row.id}`}>
                                  <td>{row.property}</td>
                                  <td>{row.date?.slice(0, 7) || '—'}</td>
                                  <td className="sr-cap">{fmtCat(row.category)}</td>
                                  <td className="sr-money sr-green">{fmtStatementAmount(row.amount)}</td>
                                  <td className="sr-note">{row.description || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={3} className="sr-foot-label">Total</td>
                                <td className="sr-money sr-green sr-foot-val">{fmtStatementAmount(incomeTotal)}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="sr-summary-col">
                      <div className="sr-summary-col-header sr-summary-col-header--expenses">
                        <span>Expenses</span>
                        <span className="sr-red">{fmtStatementAmount(expenseTotal)}</span>
                      </div>
                      {expenseRows.length === 0 ? (
                        <p className="sr-empty">No expenses this period.</p>
                      ) : (
                        <div className="sr-table-wrap">
                          <table className="sr-table">
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Date</th>
                                <th>Category</th>
                                <th>Amount</th>
                                <th>Vendor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expenseRows.map(row => (
                                <tr key={`expense-${row.id}`}>
                                  <td>{row.property}</td>
                                  <td>{row.date || '—'}</td>
                                  <td className="sr-cap">{fmtCat(row.category)}</td>
                                  <td className="sr-money sr-red">{fmtStatementAmount(row.amount)}</td>
                                  <td className="sr-note">{row.vendor || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={3} className="sr-foot-label">Total</td>
                                <td className="sr-money sr-red sr-foot-val">{fmtStatementAmount(expenseTotal)}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}

                      {ownerDistributionRows.length > 0 && (
                        <>
                          <div className="sr-summary-col-header sr-summary-col-header--owner" style={{ marginTop: 16 }}>
                            <span>Owner Distribution</span>
                            <span className="sr-owner">{fmtStatementAmount(ownerDistributionTotal)}</span>
                          </div>
                          <div className="sr-table-wrap">
                            <table className="sr-table">
                              <thead>
                                <tr>
                                  <th>Property</th>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ownerDistributionRows.map(row => (
                                  <tr key={`owner-${row.id}`}>
                                    <td>{row.property}</td>
                                    <td>{row.date || '—'}</td>
                                    <td className="sr-money sr-owner">{fmtStatementAmount(row.amount)}</td>
                                    <td className="sr-note">{row.description || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={2} className="sr-foot-label">Total</td>
                                  <td className="sr-money sr-owner sr-foot-val">{fmtStatementAmount(ownerDistributionTotal)}</td>
                                  <td />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="sr-summary-net">
                    <span className="sr-summary-net-label">Net Amount</span>
                    <span className={`sr-summary-net-val ${netTotal >= 0 ? 'sr-green' : 'sr-red'}`}>{fmtStatementAmount(netTotal)}</span>
                  </div>
                </div>
              )}

              {['statement', 'income', 'expenses'].includes(activeTab) && (
                <div className="sr-table-section">
                  <div className="sr-stmt-totals">
                    <span className="sr-stmt-total-item">
                      <span className="sr-stmt-total-label">Income</span>
                      <span className="sr-green sr-stmt-total-val">{fmtStatementAmount(incomeTotal)}</span>
                    </span>
                    <span className="sr-stmt-sep">−</span>
                    <span className="sr-stmt-total-item">
                      <span className="sr-stmt-total-label">Expenses</span>
                      <span className="sr-red sr-stmt-total-val">{fmtStatementAmount(expenseTotal)}</span>
                    </span>
                    <span className="sr-stmt-sep">=</span>
                    <span className="sr-stmt-total-item">
                      <span className="sr-stmt-total-label">Net</span>
                      <span className={`sr-stmt-total-val ${netTotal >= 0 ? 'sr-green' : 'sr-red'}`}>{fmtStatementAmount(netTotal)}</span>
                    </span>
                    <div className="sr-stmt-actions">
                  {activeTab === 'statement' && (
                        <>
                          <button className="sr-stmt-btn" onClick={() => { resetNewRow('income'); setShowAddModal(true); }}>
                            <i className="pi pi-plus" /> Income
                          </button>
                          <button className="sr-stmt-btn" onClick={() => { resetNewRow('expense'); setShowAddModal(true); }}>
                            <i className="pi pi-plus" /> Expense
                          </button>
                        </>
                      )}
                      {activeTab === 'income' && (
                        <button className="sr-stmt-btn" onClick={() => { resetNewRow('income'); setShowAddModal(true); }}>
                          <i className="pi pi-plus" /> Add Income
                        </button>
                      )}
                      {activeTab === 'expenses' && (
                        <button className="sr-stmt-btn" onClick={() => { resetNewRow('expense'); setShowAddModal(true); }}>
                          <i className="pi pi-plus" /> Add Expense
                        </button>
                      )}
                      {someVisibleStatementRowsSelected && (
                        <button className="sr-stmt-btn docs-stmt-btn--delete" onClick={() => setConfirmRowDelete({ rows: selectedVisibleStatementRows })}>
                          <i className="pi pi-trash" /> Delete ({selectedVisibleStatementRows.length})
                        </button>
                      )}
                      <button className="sr-stmt-btn" onClick={exportCSV}><i className="pi pi-download" /> CSV</button>
                      <button className="sr-stmt-btn" onClick={copyToClipboard}><i className="pi pi-copy" /> Copy</button>
                    </div>
                  </div>

                  {rowsLoading ? (
                    <PageLoader className="docs-table-loader" label="Loading statement rows" />
                  ) : displayedRows.length === 0 ? (
                    <p className="sr-empty">
                      {availablePeriods.length === 0
                        ? 'No statement data yet. Upload a parsed PDF or add a row to see data here.'
                        : 'No rows found for this period / property combination.'}
                    </p>
                  ) : (
                    <div className="docs-review-table-wrap docs-review-table-wrap--statement">
                      <DataTable
                        ref={dtRef}
                        value={displayedRows}
                        editMode="cell"
                        size="small"
                        tableStyle={{ minWidth: '920px' }}
                        rowClassName={r => r.row_type === 'income'
                          ? 'docs-row-income'
                          : (r.category === OWNER_DIST ? 'docs-row-owner-dist' : 'docs-row-expense')}
                      >
                        <Column
                          header={(
                            <input
                              type="checkbox"
                              checked={allVisibleStatementRowsSelected}
                              onChange={toggleAllStatementRows}
                            />
                          )}
                          style={{ width: '42px' }}
                          body={(rowData) => {
                            const rowKey = getStatementRowKey(rowData);
                            return (
                              <input
                                type="checkbox"
                                checked={rowKey ? selectedStatementRows.has(rowKey) : false}
                                onChange={() => toggleStatementRowSelection(rowData)}
                                onClick={(event) => event.stopPropagation()}
                              />
                            );
                          }}
                        />
                        <Column field="property" header="Property" style={{ width: '170px', fontWeight: 600, color: '#0f172a' }} />
                        <Column field="date" header="Date" style={{ width: '115px', color: '#64748b', fontVariantNumeric: 'tabular-nums' }} />
                        {activeTab === 'statement' && (
                          <Column
                            field="row_type"
                            header="Type"
                            style={{ width: '95px' }}
                            body={r => (
                              <span className={`docs-type-pill docs-type-pill--${r.row_type}`}>
                                {r.row_type === 'income' ? 'Income' : 'Expense'}
                              </span>
                            )}
                          />
                        )}
                        <Column
                          field="category"
                          header="Category"
                          style={{ width: '150px' }}
                          editor={categoryEditor}
                          onCellEditComplete={onCellEditComplete}
                          body={r => fmtCat(r.category)}
                        />
                        <Column
                          field="amount"
                          header="Amount"
                          style={{ width: '120px', textAlign: 'right' }}
                          editor={numberEditor}
                          onCellEditComplete={onCellEditComplete}
                          body={fmtAmount}
                        />
                        <Column
                          field="vendor"
                          header="Vendor"
                          style={{ width: '150px' }}
                          editor={textEditor}
                          onCellEditComplete={onCellEditComplete}
                        />
                        <Column
                          field="description"
                          header="Description"
                          editor={textEditor}
                          onCellEditComplete={onCellEditComplete}
                          body={r => r.description || ''}
                        />
                      </DataTable>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'documents' && (
                <div className="sr-table-section">
                  <div className="sr-table-header">
                    <span className="sr-table-label">Statements For {activePeriod ? new Date(`${activePeriod}-02T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Selected Period'}</span>
                    <button className="sr-add-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                      <i className="pi pi-upload" /> Upload
                    </button>
                  </div>
                  {selectedPeriodDocs.length === 0 ? (
                    <p className="sr-empty">No statement PDFs found for this period.</p>
                  ) : (
                    <div className="sr-table-wrap">
                      <table className="sr-table">
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th>File</th>
                            <th>Uploaded</th>
                            <th>Parsed</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPeriodDocs.map(doc => (
                            <tr key={doc.id}>
                              <td>{propertyLabel(doc)}</td>
                              <td>
                                <span className="docs-file-name">
                                  <i className="pi pi-file-pdf" />
                                  {doc.filename}
                                </span>
                              </td>
                              <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</td>
                              <td>
                                <span className={`docs-parsed-badge ${doc.parsed ? 'docs-parsed-badge--yes' : 'docs-parsed-badge--no'}`}>
                                  {doc.parsed ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="docs-row-actions">
                                <button type="button" className="docs-icon-btn" onClick={() => openDocument(doc)} title="Open PDF">
                                  <i className="pi pi-external-link" />
                                </button>
                                <button className="docs-icon-btn" onClick={() => downloadDocs([doc])} title="Download">
                                  <i className="pi pi-download" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="sr-ledger-section docs-ledger-section">
            <h2 className="sr-ledger-title">Full Ledger</h2>
            <div className="sr-ledger-grid">
              {availablePeriods.length === 0 ? (
                <p className="sr-empty">No records yet.</p>
              ) : availablePeriods.map(period => {
                const summary = monthlySummaryByPeriod.get(period) || {
                  income_total: 0,
                  expense_total: 0,
                  owner_distribution_total: 0,
                  net_total: 0,
                };
                const monthDocs = statementDocs.filter(doc => toPeriodKey(doc.period_month) === period);
                const isSelected = period === activePeriod;
                return (
                  <div
                    key={period}
                    className={`sr-ledger-row ${isSelected ? 'sr-ledger-row--active' : ''}`}
                    onClick={() => handlePeriodChange(period)}
                  >
                    <div className="sr-ledger-month">
                      {new Date(`${period}-02T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                    <div className="sr-ledger-stats">
                      <span className="sr-green">{fmtStatementAmount(summary.income_total)}</span>
                      <span className="sr-ledger-sep">—</span>
                      <span className="sr-red">{fmtStatementAmount(summary.expense_total)}</span>
                    </div>
                    <div className={`sr-ledger-net ${summary.net_total >= 0 ? 'sr-green' : 'sr-red'}`}>{fmtStatementAmount(summary.net_total)}</div>
                    <div className="sr-ledger-doc">
                      {monthDocs.length === 0 ? (
                        <span className="sr-no-doc">No PDF</span>
                      ) : monthDocs.length === 1 ? (
                        <div className="sr-ledger-doc-actions" onClick={(event) => event.stopPropagation()}>
                          <button
                            type="button"
                            className="sr-doc-badge"
                            onClick={() => openDocument(monthDocs[0])}
                            title={`Open ${monthDocs[0].filename}`}
                          >
                            <i className="pi pi-file-pdf" /> Statement
                          </button>
                          <button
                            type="button"
                            className="sr-doc-download"
                            onClick={() => downloadDocs([monthDocs[0]])}
                            title={`Download ${monthDocs[0].filename}`}
                            aria-label={`Download ${monthDocs[0].filename}`}
                          >
                            <i className="pi pi-download" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="sr-doc-badge docs-ledger-doc-count"
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePeriodChange(period);
                            setActiveTab('documents');
                          }}
                        >
                          <i className="pi pi-file-pdf" /> {monthDocs.length} Statements
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Add Row Modal */}
      {showAddModal && (
        <div className="docs-modal-backdrop docs-add-modal-backdrop">
          <div className="docs-add-modal">
            <div className="docs-add-modal-header">
              <div>
                <p className="docs-add-modal-eyebrow">Manual Entry</p>
                <h3>Add Statement Row</h3>
              </div>
              <button className="docs-add-close" onClick={() => setShowAddModal(false)}>
                <i className="pi pi-times" />
              </button>
            </div>
            <form className="docs-add-modal-form" onSubmit={handleAddRow}>
              <div className="docs-add-modal-grid">
                <label>
                  <span>Property</span>
                  <select
                    value={newRow.property_id}
                    onChange={e => setNewRow(r => ({ ...r, property_id: e.target.value }))}
                  >
                    <option value="">Select property…</option>
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>{p.nickname || p.address_line1}</option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Type</span>
                  <select
                    value={newRow.row_type}
                    onChange={e => {
                      const nextType = e.target.value;
                      setNewRow(r => ({
                        ...r,
                        row_type: nextType,
                        category: nextType === 'income' ? INCOME_CATS[0] : EXPENSE_CATS[0],
                        date: nextType === 'income'
                          ? (activePeriod || r.date?.slice(0, 7) || '')
                          : (r.date?.length === 7 ? `${r.date}-01` : r.date || (activePeriod ? `${activePeriod}-01` : '')),
                      }));
                    }}
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </label>

                <label>
                  <span>{newRow.row_type === 'income' ? 'Month' : 'Date'}</span>
                  <input
                    type={newRow.row_type === 'income' ? 'month' : 'date'}
                    value={newRow.date}
                    onChange={e => setNewRow(r => ({ ...r, date: e.target.value }))}
                  />
                </label>

                <label>
                  <span>Category</span>
                  <select
                    value={newRow.category}
                    onChange={e => setNewRow(r => ({ ...r, category: e.target.value }))}
                  >
                    {(newRow.row_type === 'income' ? INCOME_CATS : EXPENSE_CATS).map(c => (
                      <option key={c} value={c}>{fmtCat(c)}</option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Amount</span>
                  <InputNumber
                    value={newRow.amount}
                    onValueChange={e => setNewRow(r => ({ ...r, amount: e.value }))}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    inputClassName="docs-add-amount-input"
                    placeholder="0.00"
                  />
                </label>

                {newRow.row_type === 'expense' && (
                  <label>
                    <span>Vendor</span>
                    <InputText
                      value={newRow.vendor || ''}
                      onChange={e => setNewRow(r => ({ ...r, vendor: e.target.value }))}
                    />
                  </label>
                )}

                <label className="docs-add-span2">
                  <span>{newRow.row_type === 'income' ? 'Notes' : 'Description'}</span>
                  <InputText
                    value={newRow.description || ''}
                    onChange={e => setNewRow(r => ({ ...r, description: e.target.value }))}
                  />
                </label>
              </div>

              <div className="docs-add-modal-actions">
                <button type="button" className="docs-add-reset" onClick={() => resetNewRow(newRow.row_type)}>
                  Clear
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" className="docs-add-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="docs-add-btn">
                  <i className="pi pi-check" /> Save Row
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
